import dayjs from "dayjs";
import { TFunction } from "i18next";
import { ReactNode } from "react";
import { UseFormReturn } from "react-hook-form";

import { PickerDate } from "../../../analyze/context";
import { FilePrefix, LanguageCode } from "../../types";
import { OrientationType } from "../components/background/BackgroundUploadCard";
import { IApiGamesToggle } from "./game-interfaces";
import { IApiGreeting, IApiGreetingsWithToggle } from "./greeting-interfaces";

// ==================== Kiosk Core Settings ====================

export interface IUpdateKioskReq {
    character_id: string;
    marquee?: IMarquee;
    native?: IKioskSettingNative;
}

export interface IKioskSettingReq {
    character_id: string;
}

export interface IKioskSettingRes {
    status: number;
    message_key: string;
    message: string;
    data: {
        id: string;
        character_id: string;
        marquee: IMarquee;
        native: IKioskSettingNative;
        greetings?: IApiGreeting[];
        web: null;
        last_updated_by: string;
        created_at: string;
        updated_at: string;
    };
}

export interface IKioskSettingNative {
    general: {
        defaultLanguage?: string;
        languageOptions: string[];
        languageDisplayMode?: LanguageDisplayMode;
    };
    schedule_marquee?: IScheduleMarqueeItem[] | IScheduleMarqueeNew;
    ad?: IApiAdvertisement;
    greetings?: IApiGreeting[] | IApiGreetingsWithToggle;
    hints?: IApiHints;
    QRcode?: IApiQRCode;
    festival_setting?: IApiFestivalSetting;
    photo?: IApiPhotoSettings;
    background?: IApiBackground;
    volume?: IApiVolume;
    weather?: IApiWeather;
    time_alerts?: IApiTimeAlerts;
    interaction?: IApiInteraction;
    layout_mode?: IApiLayoutMode;
    subtitle_speed?: IApiSubtitleSpeed[];
    bidirectional_translation?: IApiBidirectionalTranslation;
    games?: IApiGamesToggle;
    character_avatar?: IApiCharacterAvatar;
}

export interface IKioskData {
    id?: string;
    character_id?: string;
    marquee?: IMarquee;
    native?: {
        general?: {
            defaultLanguage?: string;
            languageOptions: string[];
            languageDisplayMode?: LanguageDisplayMode;
        };
        schedule_marquee?: IScheduleMarqueeItem[] | IScheduleMarqueeNew;
        ad?: IApiAdvertisement;
        greetings?: IApiGreeting[] | IApiGreetingsWithToggle;
        hints?: IApiHints;
    };
    web?: null;
    last_updated_by?: string;
    created_at?: string;
    updated_at?: string;
}

export interface IKioskTabRoute {
    labelKey: string;
    href: string;
}

export interface IModuleProps {
    characterId: string;
}
// ==================== Upload/File ====================

export interface IUploadFileParams {
    file: File;
    prefix: FilePrefix;
}

export interface IUploadFileRes {
    url: string;
    asset_id: string;
}
// ==================== Time Alerts Settings (Form & Props) ====================

export type WeekdayCode = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export type TimeAlertsGroupType = "voice" | "media";

export interface IApiTimeAlertVoiceSetting {
    country: string;
    station_code: string;
    station_name: string;
}

export interface IApiTimeAlertMediaSetting {
    url: string;
    asset_id: string;
    type: "image" | "video";
}

/** Form-only: media with optional selected file before upload (not sent to API) */
export interface ITimeAlertMediaFormSetting extends IApiTimeAlertMediaSetting {
    file?: File;
}

export interface IApiTimeAlertGroup {
    id: string;
    time: string;
    repeat_days: WeekdayCode[];
    type: TimeAlertsGroupType;
    voice?: IApiTimeAlertVoiceSetting;
    media?: IApiTimeAlertMediaSetting;
}

export interface IApiTimeAlerts {
    enabled_time_alerts: boolean;
    data: IApiTimeAlertGroup[];
}

export type ITimeAlertGroup = IApiTimeAlertGroup;

/** Form-only: group with media that may include selected file */
export type ITimeAlertGroupForm = Omit<IApiTimeAlertGroup, "media"> & {
    media?: ITimeAlertMediaFormSetting;
};

export interface ITimeAlertFormData {
    enabled_time_alerts: boolean;
    data: ITimeAlertGroupForm[];
}


export interface TimeAlertsSettingsModuleProps {
    characterId: string;
}

export interface TimeAlertGroupCardProps {
    index: number;
    group: ITimeAlertGroupForm;
    timeError: string | null;
    t: (key: string, options?: Record<string, unknown>) => string;
}
// ==================== Language Settings ====================

export type LanguageDisplayMode = "dropdown" | "expand";

export interface ILanguageFormData {
    languageOptions: string[];
    defaultLanguage: string;
    languageDisplayMode: LanguageDisplayMode;
}

export interface LanguageSettingsModuleProps {
    characterId: string;
}

export interface LanguageOption {
    label: string;
    value: string;
}

export interface LanguageContextType {
    languageOptions: LanguageOption[];
    isLoadingLanguageOptions: boolean;
    getAvailableLanguageOptions: (currentIndex: number) => LanguageOption[];
    getUsedLanguagesByOthers: (currentIndex: number) => string[];
}

export interface LanguageProviderProps {
    children: ReactNode;
    languageOptions: LanguageOption[];
    isLoadingLanguageOptions: boolean;
    getAvailableLanguageOptions: (currentIndex: number) => LanguageOption[];
    getUsedLanguagesByOthers: (currentIndex: number) => string[];
}
// ==================== Marquee Settings ====================

export interface IMarquee {
    enable_marquee: boolean;
    color_bg: string;
    color_text: string;
    speed: number;
    datas:
        | {
              language: string;
              text: string;
          }[]
        | null;
}

export interface IScheduleMarqueeItem {
    enable_marquee: boolean;
    color_bg: string;
    color_text: string;
    speed: string;
    start_at: string;
    end_at: string;
    datas: Array<{
        language: string;
        text: string;
    }>;
}

export interface ILanguageContent {
    speed: number;
    text: string;
}

export interface IMarqueeContentItem {
    [languageCode: string]: ILanguageContent;
}

export interface IMarqueeDataItem {
    color_bg: string;
    color_text: string;
    start_at: string;
    end_at: string;
    content: IMarqueeContentItem[];
    selectedLanguages?: string[];
}

export type MarqueePosition = "top" | "bottom";

export interface IScheduleMarqueeNew {
    enable_marquee: boolean;
    position: MarqueePosition;
    data: IMarqueeDataItem[];
}

export interface IScheduleMarqueeFormDataNew {
    schedule_marquee: IScheduleMarqueeNew;
}

export const isNewMarqueeFormat = (
    scheduleMarquee: IScheduleMarqueeItem[] | IScheduleMarqueeNew | undefined
): scheduleMarquee is IScheduleMarqueeNew => {
    if (!scheduleMarquee) return false;
    return (
        typeof scheduleMarquee === "object" &&
        !Array.isArray(scheduleMarquee) &&
        "enable_marquee" in scheduleMarquee &&
        "data" in scheduleMarquee &&
        Array.isArray(scheduleMarquee.data)
    );
};

export const isOldMarqueeArrayFormat = (
    scheduleMarquee: IScheduleMarqueeItem[] | IScheduleMarqueeNew | undefined
): scheduleMarquee is IScheduleMarqueeItem[] => {
    if (!scheduleMarquee) return false;
    return Array.isArray(scheduleMarquee) && scheduleMarquee.length > 0;
};

export const hasLegacyMarquee = (kioskData: IKioskData): boolean => {
    return Boolean(kioskData?.marquee);
};

export type DateChangeHandler = (groupIndex: number, field: "start" | "end", date: PickerDate) => void;

export interface IMarqueeValidationError {
    type: "start_before_today" | "end_before_start" | "time_overlap";
    message: string;
    conflictGroupIndex?: number;
}

export interface ValidateMarqueeGroupProps {
    t: TFunction;
    groupIndex: number;
    groupData: IMarqueeDataItem;
    allGroupsData: IMarqueeDataItem[];
    MIN_DATE: dayjs.Dayjs;
}

export interface IMarqueeGroupSettingsProps {
    groupIndex: number;
    enableMarquee: boolean;
    currentGroup: scheduleMarqueeGroup | undefined;
    handleDateRangeChange: DateChangeHandler;
    minDate: dayjs.Dayjs;
    maxDate: dayjs.Dayjs;
}

export interface MarqueeSettingsModuleProps {
    characterId: string;
}

export interface scheduleMarqueeGroup {
    id: number;
    title: string;
    dateRange: string;
    startDate: dayjs.Dayjs;
    endDate: dayjs.Dayjs;
    validationErrors?: IMarqueeValidationError[];
}
// ==================== Hint Settings ====================
export type HintsIconType = "finger" | "speaker";

export interface IApiHintLanguage {
    language: LanguageCode;
    options: string[];
}

/** @deprecated 請改用 IApiHintsObject —— 本型別為舊版陣列格式，僅供相容 */
export type IApiHintsArray = IApiHintLanguage[];

export interface IApiHintsObject {
    hints_icon: HintsIconType;
    data: IApiHintLanguage[];
}

export type IApiHints = IApiHintsArray | IApiHintsObject;

export const isHintsObjectFormat = (hints: IApiHints | undefined): hints is IApiHintsObject => {
    if (!hints) return false;
    return typeof hints === "object" && !Array.isArray(hints) && "data" in hints && "hints_icon" in hints;
};

export interface IHintFormData {
    hints_icon: HintsIconType;
    hints: Array<{
        language: LanguageCode;
        option1: string;
        option2: string;
        option3: string;
    }>;
}

export interface HintSettingsModuleProps {
    characterId: string;
}
// ==================== Advertisement Settings ====================

export interface IApiAdMedia {
    key: string;
    name: string;
    type: "image" | "video";
    duration: number | null;
    url: string;
    updated_at: string;
    is_permanent: boolean;
    start_at: string | null;
    end_at: string | null;
}

export interface IApiAdvertisement {
    enabled: boolean;
    shuffle_mode: boolean;
    media: IApiAdMedia[];
}

export interface IAdMedia {
    key: string;
    name: string;
    type: "image" | "video";
    duration: number | null;
    url: string;
    updated_at: string;
    is_permanent: boolean;
    start_at: PickerDate;
    end_at: PickerDate;
}

export interface IAdFormData {
    enabled: boolean;
    shuffle_mode: boolean;
    media: IAdMedia[];
}

export interface AdSettingsModuleProps {
    characterId: string;
}

export interface IPlaylistDragItem {
    [key: string]: unknown;
    id: string;
    key: string;
    index: number;
    mediaType: "image" | "video";
    mediaUrl: string;
    mediaName: string;
}

export interface AdvertiseMediaCardProps {
    index: number;
    media: IAdMedia;
    dateError: string | null;
    validateDateRange: (start: dayjs.Dayjs | null, end: dayjs.Dayjs | null) => string | null;
    getMediaDateRange: (media: IAdMedia) => { started_at: dayjs.Dayjs | null; ended_at: dayjs.Dayjs | null };
    handleRemoveMedia: (index: number) => void;
    t: (key: string) => string;
}

export interface AdvertisePlaylistItemProps {
    item: IPlaylistDragItem;
    index: number;
    displayName: string;
    displayType: "image" | "video";
    isPermanent?: boolean;
    startAt?: dayjs.Dayjs | null;
    endAt?: dayjs.Dayjs | null;
    t: (key: string) => string;
}
// ==================== QR Code Settings ====================

export interface IApiQRCode {
    QRcode_show_mode: boolean;
    QRcode_Url: string;
    inputMode?: "url" | "image";
    updated_at?: string;
}

export type QRCodeInputMode = "url" | "image";

export interface IQRCodeFormData {
    QRcode_show_mode: boolean;
    inputMode: QRCodeInputMode;
    url: string;
    imageFile: File | null;
    imageUrl: string;
}

export interface QRCodeInputProps {
    methods: UseFormReturn<IQRCodeFormData>;
    watchedInputMode: QRCodeInputMode;
    watchedImageFile: File | null;
    watchedImageUrl: string | null;
    dimensionError: string | null;
    QR_IMAGE_MIN_SIZE: number;
    QR_IMAGE_MAX_SIZE: number;
    handleModeChange: (mode: QRCodeInputMode) => void;
    handleFileChange: (file: File | null) => Promise<boolean>;
}

export interface ImageUploadPreviewProps {
    fileInputRef: React.RefObject<HTMLInputElement>;
    onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
    watchedImageFile: File | null;
    watchedImageUrl: string | null;
    previewUrl: string | null;
    handleRemoveFile: () => void;
    dimensionError: string | null;
    QR_IMAGE_MIN_SIZE: number;
    QR_IMAGE_MAX_SIZE: number;
}

export interface QRCodeSettingsModuleProps {
    characterId: string;
}
// ==================== Festival Settings ====================

export interface IApiFestivalSettingOptions {
    bgm: boolean;
    bg: boolean;
    bg_obj: boolean;
    effect: boolean;
    character: boolean;
    character_is_conservative: boolean;
    filter: boolean;
    filter_keep_default: boolean;
}

export interface IApiFestivalSetting {
    use_festival: boolean;
    setting: IApiFestivalSettingOptions;
}

export interface IFestivalFormData {
    use_festival: boolean;
    bgm: boolean;
    bg: boolean;
    bg_obj: boolean;
    effect: boolean;
    character: boolean;
    character_is_conservative: boolean;
    filter: boolean;
    filter_keep_default: boolean;
}

export interface IFestivalScheduleItem {
    year: number;
    name: string;
    key: string;
    startDate: string;
    endDate: string;
    description: string;
}

export interface FestivalSettingsModuleProps {
    characterId: string;
}
// ==================== Photo Filter Settings ====================

export interface IApiPhotoFilter {
    customized: boolean;
    filters_url: string[];
    enable_default: boolean;
}

export interface IApiPhotoSettings {
    enabled_photo: boolean;
    filter: IApiPhotoFilter;
    character: {
        enable: boolean;
    };
    line_id: string;
    take_photo_sec: number;
    close_result_sec: number;
    updated_at?: string;
}

export interface ICustomFilterItem {
    key: string;
    url: string;
    file: File | null;
}

export interface IPhotoFilterFormData {
    enabled_photo: boolean;
    customized: boolean;
    customFilters: ICustomFilterItem[];
    enable_default: boolean;
    take_photo_sec: number;
    close_result_sec: number;
    character_enable: boolean;
    line_enabled: boolean;
}

export interface IDefaultFilterItem {
    key: string;
    name: string;
    url: string;
}

export interface PhotoFilterSettingsModuleProps {
    characterId: string;
}
// ==================== Background Settings ====================

export type BackgroundMode = "single" | "separate";

export interface IApiBackgroundImage {
    url: string;
    updated_time: string;
    position_x: number;
    position_y: number;
}

export interface IApiBackground {
    mode?: BackgroundMode;
    v: IApiBackgroundImage;
    h: IApiBackgroundImage;
}

export interface IBackgroundImageFormData {
    file: File | null;
    url: string;
}

export interface IBackgroundFormData {
    portrait: IBackgroundImageFormData;
    landscape: IBackgroundImageFormData;
}

export interface BackgroundSettingsModuleProps {
    characterId: string;
}

export interface BackgroundUploadCardProps {
    type: OrientationType;
    label: string;
    displayUrl: string;
    hasImage: boolean;
    onFileSelect: (type: OrientationType, file: File | null) => void;
}

// Deprecated (該元件 FocalPointSelecto目前不使用)
export interface FocalPointSelectorProps {
    imageUrl: string;
    positionX: number;
    positionY: number;
    onPositionChange: (x: number, y: number) => void;
    disabled?: boolean;
}

// ==================== Volume/Voice Settings ====================

export interface IApiVolume {
    bgm_url: string;
    bgm_max: number;
    bgm_min: number;
    ad_video_max: number;
    ad_video_min: number;
    voice: number;
    effect: number;
}

export interface IVolumeFormData {
    bgm_file: File | null;
    bgm_url: string;
    bgm_max: number;
    bgm_min: number;
    ad_video_max: number;
    ad_video_min: number;
    voice: number;
    effect: number;
}

export interface VolumeSettingsModuleProps {
    characterId: string;
}

// ==================== Weather Settings (Form & Props) ====================

export interface IWeatherFormData {
    enabled_weather: boolean;
    display_description: boolean;
    station_name: string;
    station_code: string;
    country: string;
}

export interface IApiWeather {
    enabled_weather: boolean;
    display_description: boolean;
    station_name: string;
    station_code: string;
    country: string;
}

export interface WeatherSettingsModuleProps {
    characterId: string;
}
// ==================== Interaction Settings (Form & Props) ====================

export type InteractionMode = "touch" | "gesture" | "custom";

export interface IInteractionFormData {
    enabled_interaction: boolean;
    interaction_mode: InteractionMode;
    data: Array<{
        language: string;
        content: string;
    }>;
}

export interface InteractionSettingsModuleProps {
    characterId: string;
}

export interface IApiInteractionData {
    language: string;
    content: string;
}

export interface IApiInteraction {
    enabled_interaction: boolean;
    interaction_mode: InteractionMode;
    data: IApiInteractionData[];
}

// ==================== Layout Mode Settings (Form & Props) ====================

export interface ILayoutModeFormData {
    mode: LayoutModeType;
    ad_position: AdPosition;
}

export interface LayoutSettingsModuleProps {
    characterId: string;
}

export interface LayoutModeCardProps {
    mode: LayoutModeType;
    label: string;
    previewUrl: string;
    isSelected: boolean;
    onSelect: (mode: LayoutModeType) => void;
}

export type LayoutModeType = "standard" | "guide" | "broadcast" | "section";

export type AdPosition = "left" | "right" | "top" | "bottom";

export interface IApiLayoutMode {
    mode: LayoutModeType;
    ad_position?: AdPosition;
}

// ==================== Subtitle Speed Settings ====================

export interface IApiSubtitleSpeed {
    language: string;
    speed: number;
}

export interface ISubtitleSpeedFormData {
    subtitle_speed: IApiSubtitleSpeed[];
}

export interface SubtitleSpeedSettingsModuleProps {
    characterId: string;
}

// ==================== Bidirectional Translation Settings====================
export interface IBidirectionalTranslationFormData {
    enabled: boolean;
}

export interface IApiBidirectionalTranslation {
    enabled: boolean;
}

export interface BidirectionalTranslationSettingsModuleProps {
    characterId: string;
}

// ==================== Character Avatar Settings ====================

export type CharacterAvatarMode = "2d" | "3d";

export interface IApiAvatar2D {
    idle_video_url: string;
    talking_video_url: string;
    updated_at: string;
}

export interface IApiAvatar3D {
    name: string;
    url: string;
    updated_at: string;
}

export interface IApiCharacterAvatar {
    mode: CharacterAvatarMode;
    avatar_2d: IApiAvatar2D;
    avatar_3d: IApiAvatar3D;
}

export interface IAvatar2DFormData {
    idle_video_url: string;
    talking_video_url: string;
    idleVideoFile: File | null;
    talkingVideoFile: File | null;
}

export interface IAvatar3DFormData {
    name: string;
    url: string;
    vrmFile: File | null;
}

export interface ICharacterAvatarFormData {
    mode: CharacterAvatarMode;
    avatar_2d: IAvatar2DFormData;
    avatar_3d: IAvatar3DFormData;
}

export interface CharacterAvatarSettingsModuleProps {
    characterId: string;
}
